import React, { useState } from "react";
import { Stack } from "@mui/material/";
import { DataGrid } from "@mui/x-data-grid";
import { DebounceInput } from "react-debounce-input";
import { Template } from "./conflict-mineral/Template";
import { InputFile } from "./conflict-mineral/InputFile";
import { Form, Button, Col } from "@deere/ux.uxframe-react";
import {
  PART_COLUMN,
  PART_TEMPLATE_COLUMNS,
  DATA_VALIDATE_SUCCESS,
  PART_DESCRIPTION_COLUMN,
  PART_SUPPLIER_NUMBER_COLUMN,
  NON_DEERE_PART_NUMBER_COLUMN,
  NON_DEERE_COMPANY_EMAIL_COLUMN,
  NON_DEERE_SUPPLIER_NAME_COLUMN,
  NON_DEERE_SUPPLIER_EMAIL_COLUMN,
  NON_DEERE_NON_PART_TEMPLATE_NAME,
  NON_DEERE_SUPPLIER_NUMBER_COLUMN,
  TEMPLATE_NON_PART_NON_DEERE_COLUMNS,
} from "../utils/surveyLiterals";
import { createNonDeereAssignments, validateNonDeereDuplicates, validateSupplierPart } from "../utils/fileUtil";
import { ERR_COLUMNS_REQUIRED, ERR_RECORDS_REQUIRED, ERR_ROW_DUPLICATED, NON_PART_NON_DEERE_ERR_RECORDS_REQUIRED } from "../utils/errorLiterals";

export const GenericCard = ({ loading, setLoading, onChange, validateSupplierNumbers, campaign, setSuccessMessage, setErrorMessage, setValidated, setDisableSubmit, userInfo }) => {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);

  const validateSupplierNumbersNonDeere = () => {
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    if (!Array.isArray(data) || data.length <= 0) {
      setValidated(true);
      setDisableSubmit(true);
      setErrorMessage(ERR_SUPPLIER_REQUIRED);
      setLoading(false);
      return;
    }
    if (campaign.isNonDeere) {
      const colSupplierNumber = columns.find((col) => col.headerName === NON_DEERE_SUPPLIER_NUMBER_COLUMN);
      const colSupplierName = columns.find((col) => col.headerName === NON_DEERE_SUPPLIER_NAME_COLUMN);
      const colSupplierEmail = columns.find((col) => col.headerName === NON_DEERE_SUPPLIER_EMAIL_COLUMN);
      const colCompanyEmail = columns.find((col) => col.headerName === NON_DEERE_COMPANY_EMAIL_COLUMN);
      if (!Array.isArray(columns) || !colSupplierNumber || !colSupplierName || !colSupplierEmail || !colCompanyEmail) {
        setErrorMessage(`${ERR_COLUMNS_REQUIRED}: ${NON_DEERE_SUPPLIER_NUMBER_COLUMN}, ${NON_DEERE_SUPPLIER_NAME_COLUMN}, ${NON_DEERE_SUPPLIER_EMAIL_COLUMN}, ${NON_DEERE_COMPANY_EMAIL_COLUMN}`);
        setValidated(true);
        setDisableSubmit(true);
        setLoading(false);
        return;
      }
    }
    const duplicates = validateNonDeereDuplicates(data, NON_DEERE_SUPPLIER_NUMBER_COLUMN, NON_DEERE_PART_NUMBER_COLUMN);
    const colsToValidate = [PART_SUPPLIER_NUMBER_COLUMN, PART_COLUMN, PART_DESCRIPTION_COLUMN];
    const nonDeereColsToValidate = [NON_DEERE_SUPPLIER_EMAIL_COLUMN, NON_DEERE_COMPANY_EMAIL_COLUMN, NON_DEERE_SUPPLIER_NUMBER_COLUMN, NON_DEERE_SUPPLIER_NAME_COLUMN];
    const validSupplierPart = validateSupplierPart(data, campaign.isNonDeere ? nonDeereColsToValidate : colsToValidate);
    if (validSupplierPart.length > 0) {
      setValidated(true);
      setDisableSubmit(true);
      if (!campaign.isNonDeere) {
        setErrorMessage(`${ERR_RECORDS_REQUIRED} : ${validSupplierPart.map((element) => element)}`);
      } else {
        setErrorMessage(`${NON_PART_NON_DEERE_ERR_RECORDS_REQUIRED} : ${validSupplierPart.map((element) => element)}`);
      }
      setLoading(false);
      return;
    }
    if (duplicates.length > 0) {
      setValidated(true);
      setDisableSubmit(true);
      setErrorMessage(`${ERR_ROW_DUPLICATED} : ${duplicates.map((element) => element)}`);
      setLoading(false);
      return;
    }
    if (campaign.isNonDeere) {
      setValidated(false);
      setDisableSubmit(false);
      campaign.assignments = createNonDeereAssignments(data, userInfo);
      setSuccessMessage(DATA_VALIDATE_SUCCESS);
      setLoading(false);
    }
  };

  if (campaign.isNonDeere) {
    return (
      <>
        <Form.Row>
          <Form.Group as={Col}>
            <Form.Row>
              <Form.Group as={Col}>
                <Form.Label className="uxf-label">Supplier</Form.Label>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <InputFile setData={setData} setColDefs={setColumns} loading={loading} />
                </Stack>
              </Form.Group>
              <Form.Group as={Col}>
                <Form.Label className="uxf-label">Supplier Template</Form.Label>
                <Form.Row>
                  <Template csvData={campaign.isNonDeere ? TEMPLATE_NON_PART_NON_DEERE_COLUMNS : PART_TEMPLATE_COLUMNS} fileName={NON_DEERE_NON_PART_TEMPLATE_NAME} />
                </Form.Row>
              </Form.Group>
            </Form.Row>
            <Form.Row>
              <DataGrid autoHeight={true} rows={data} columns={columns} disableColumnMenu={true} rowsPerPageOptions={[]} pageSize={10} />
            </Form.Row>
          </Form.Group>
        </Form.Row>
        <Form.Row className="d-block mb-4">
          <Form.Group as={Col} className="align-right">
            <Button variant="secondary" className="float-right" onClick={validateSupplierNumbersNonDeere} disabled={loading}>
              {campaign.isNonDeere ? "Validate Data" : "Validate Supplier Numbers"}
            </Button>
          </Form.Group>
        </Form.Row>
      </>
    );
  }

  return (
    <>
      <Form.Row className="d-block mb-4">
        <Form.Label className="uxf-label">Supplier Numbers </Form.Label>
        <small style={{ color: "gray" }}> (One entry per line)</small>
        <DebounceInput element="textarea" name="supplierNumbers" required className="form-control" rows="4" onChange={onChange} disabled={loading} debounceTimeout={300} />
      </Form.Row>
      <Form.Row className="d-block mb-4">
        <Form.Group as={Col} className="align-right">
          <Button variant="secondary" className="float-right" onClick={validateSupplierNumbers} disabled={loading}>
            Validate Supplier Numbers
          </Button>
        </Form.Group>
      </Form.Row>
    </>
  );
};
